import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import { BriefingGrade, GRADE_LABEL } from './mockData';
import { BriefingItemDto, CoachOnboardingRow } from '../../types/opsBriefing';
import { toCardView, sortCards } from './coachOnboarding';
import {
    composeBriefing,
    beforeValueOf,
    canCompareDelta,
    BEFORE_UNCOVERED_DELTA_TEXT,
    BRIEFING_FOCUS_COUNT,
    MAX_SETUP_SLOTS,
    SetupTaskSlot,
} from './briefingComposition';
import MiniTrendBars from './MiniTrendBars';

dayjs.locale('ko');

/**
 * 아침 브리핑 팝업.
 *
 * 「오늘은 그만 보기」를 누르기 전까지는 화면을 옮길 때마다 계속 뜬다.
 *
 * 요약만 보여주면 "자세히 보기"를 누를 이유가 생기지 않는다.
 * 그래서 여기서도 판단에 필요한 수치(이전 → 현재, 변화율, 추이)를 다 보여주고,
 * 액션은 상세 화면에서 하도록 유도한다.
 *
 * ─────────────────────────────────────────────────────────────
 * 다섯 건만 세운다 (2026-08 변경)
 * ─────────────────────────────────────────────────────────────
 * 예전에는 그날 올라온 것을 전부 늘어놓고 머리말에 "오늘 급하게 챙길 일이 29건
 * 있습니다" 라고 적었다. 그 숫자는 정보가 아니라 겁주는 숫자다 —
 * 아침에 팝업을 여는 순간 원장님이 할 수 있는 일은 스물아홉 건이 아니라
 * 서너 건이고, 스물아홉이라고 적으면 그중 무엇부터인지가 오히려 사라진다.
 *
 * 그래서 **오늘의 브리핑 화면과 같은 편성 함수**(briefingComposition)를 써서
 * 위험 순 다섯 건만 세우고 나머지는 "외 N건" 이라고 밝힌다.
 * 두 화면이 다른 다섯 건을 고르면 팝업에서 본 회원이 화면에 없는 일이 생긴다.
 *
 * ⚠️ 팝업에는 강사 인사이트가 없다. 이 컴포넌트를 띄우는 게이트는 브리핑만
 *    조회하고 강사 현황은 부르지 않는다 (모든 화면에서 도는 컴포넌트라
 *    조회를 더 늘리지 않는다). 그래서 여기서는 브리핑 항목 다섯 건이다.
 */

const GRADE_COLOR: Record<BriefingGrade, { accent: string; bg: string; rail: string }> = {
    DANGER: { accent: '#FF3E4A', bg: '#FFE8E9', rail: '#FF3E4A' },
    WARNING: { accent: '#B45309', bg: '#FEF3C7', rail: '#F0A020' },
    CHANCE: { accent: '#3D73DD', bg: '#ECF1FC', rail: '#3D73DD' },
    PRAISE: { accent: '#00A793', bg: '#E5F8F3', rail: '#00A793' },
};

type Props = {
    /*
     * 목 타입(BriefingItem)이 아니라 서버 계약(BriefingItemDto)을 받습니다.
     * 목 타입을 받으면 목 데이터만 넣을 수 있어, 실제로 그렇게 돼 있었습니다.
     */
    items: BriefingItemDto[];
    /**
     * 세팅 과제 **목록** (2026-09-01 QA23 → QA34).
     *
     * 신규 센터는 회원 항목이 0건이라 예전에는 팝업이 아예 안 떴다. 그런데 그
     * 센터에도 **오늘 할 일은 있다** — 강사 연결·회원 배정 같은 세팅 과제다.
     *
     * 🔴 **건수가 아니라 목록을 받는다.** 예전에는 이미 세어진 수를 받아서
     *    그대로 더했는데, 그 수는 자리 규칙을 통과하기 **전**의 날것이었다.
     *    정착 센터에서 팝업이 「17건」이라 말하고 화면은 다섯 건이었다.
     *    목록을 받아 화면과 **같은 함수**에 먹인다.
     */
    setupTasks?: SetupTaskSlot[];
    /**
     * 신규 센터의 **강사 카드 원본** (2026-09-01 대표 시안).
     *
     * 🔴 있으면 **이쪽이 목록이 된다** — 회원 항목도 세팅 축 카드도 아니다.
     *    세팅이 안 끝난 센터에는 분석할 회원이 없어서, 그날 할 일이 전부
     *    강사별 세팅이기 때문이다. 화면과 같은 판정을 쓴다.
     *
     * ⚠️ 여기서는 **다섯 건으로 자르지 않는다.** 신규 센터는 그 목록이
     *    그날 할 일의 전부라, 자르면 원장님이 나머지를 못 본다
     *    (화면도 전부 세운다 — 대표 확정).
     */
    onboardingCoaches?: CoachOnboardingRow[] | null;
    /** 일단 닫기 — 다른 화면으로 이동하면 다시 뜬다 */
    onDismiss: () => void;
    /** 오늘은 그만 보기 — 오늘 하루 뜨지 않는다 */
    onMuteToday: () => void;
    /**
     * 🔴 브리핑 탭이 **2차 비밀번호로 잠겨 있는가.**
     *
     * 잠겨 있으면 **건수만** 보여주고 회원 이름·수치·금액은 하나도 안 그립니다
     * (아래 「가린 팝업」). 탭을 잠가 뒀는데 팝업이 같은 내용을 띄우면 잠근
     * 의미가 없습니다 — 자리를 비운 사이 PC 앞에 누가 앉든 그대로 보입니다.
     *
     * 그렇다고 안 띄우면 **첫 로그인에 팝업이 아예 안 뜹니다**(탭 기본값이
     * 잠금이라 대부분의 브라우저가 이 상태입니다). 둘 다 대표 결정이라
     * 어느 한쪽을 무력화할 수 없어, 「가리고 띄운다」로 둘을 다 지킵니다.
     */
    locked?: boolean;
};

const MorningBriefingModal = ({
    items,
    onDismiss,
    onMuteToday,
    locked = false,
    setupTasks = [],
    onboardingCoaches = null,
}: Props) => {
    const navigate = useNavigate();

    const today = dayjs();
    const weekday = ['일', '월', '화', '수', '목', '금', '토'][today.day()];

    /*
     * 오늘의 브리핑 화면과 **같은 편성**. 두 곳이 다른 다섯 건을 고르면
     * 팝업에서 본 회원이 화면에는 없다.
     * 원장이 가린 항목(DISMISSED)도 같은 함수가 걸러내므로,
     * 목록에서 가린 건이 팝업에 그대로 떠 있는 일이 생기지 않는다.
     */
    const composed = composeBriefing(items, null, BRIEFING_FOCUS_COUNT, setupTasks);
    /*
     * focus 는 이제 **전부 회원 항목**이다 (2026-08-28 통일 모델).
     * 합성 카드는 focus 밖으로 나가서, 팝업·오늘의 브리핑·지난 리포트가
     * **같은 다섯 명**을 말한다. 예전에는 여기서 합성 카드를 걸러내야 했고
     * 그만큼 팝업의 다섯 건이 오늘의 브리핑과 달랐다.
     */
    const focus = composed.focus.map((slot) => slot.item);
    const restCount = composed.rest.length;

    /* 머리말의 숫자는 **오늘 세운 다섯 건** 기준이다. 전체 건수는 아래에서 사실로만 밝힌다 */
    const urgent = focus.filter((i) => i.grade === 'DANGER').length;
    const memberCount = focus.filter((i) => i.subjectType === 'MEMBER').length;
    const coachCount = focus.filter((i) => i.subjectType === 'COACH').length;

    /*
     * 🔴 팝업이 세는 「오늘 챙길 일」 = **화면이 세우는 것과 같은 목록**.
     *
     * 회원 항목이 0건이어도 세팅 과제가 있으면 그게 오늘의 할 일이다.
     * 그때는 무엇에 대한 건수인지 밝힌다 — 「1건 있습니다」만 적으면 원장님은
     * 회원 한 명을 찾으러 들어갔다가 못 찾는다.
     *
     * ⚠️ **`composed.setupTasks` 를 쓴다. 넘겨받은 원본을 세지 마라.**
     *    자리 규칙이 여기 걸려 있다 (`composeBriefing` 의 `setupLimit`):
     *      정착(회원 5건)   → 과제 0장   → 5건
     *      과도기(회원 3건) → 과제 2장   → 5건
     *      신규(회원 0건)   → 과제 전부  → 7건 (상한을 푸는 유일한 경우)
     *    원본을 세면 정착 센터가 17건이 된다 — 실제로 그랬다(QA34).
     */
    const setupShown = composed.setupTasks;
    const setupCount = setupShown.length;

    /*
     * 🔴 신규 센터면 **강사 카드가 목록 전체를 대체한다** (대표 시안).
     *    화면과 같은 함수로 같은 판정을 한다 — 순항 강사는 챙길 일이 아니다.
     */
    const coachChores = onboardingCoaches
        ? sortCards(onboardingCoaches.map((c) => toCardView(c, today))).filter((v) => v.chore)
        : [];
    /*
     * ─────────────────────────────────────────────────────────────
     * 🔴 **회원 카드가 하나도 없을 때만** 대체한다 (2026-09-02 QA36)
     * ─────────────────────────────────────────────────────────────
     * 「신규 센터면」이라고 적어 두고 정작 조건은 `coachChores.length > 0`
     * 하나였다. 그 목록은 「세팅 미졸업」으로 골라지는데(`MorningBriefingGate`),
     * 수강권 없는 회원이 한 명만 있어도 미졸업이라 **정착 센터에서도 늘 참**
     * 이었다. 결과: 챙길 회원 15건이 있는 아침에도 팝업이 강사 온보딩
     * 목록으로 통째로 바뀌었다.
     *
     * 판정은 화면과 **같은 계산기**에서 읽는다 — `composeBriefing` 의 단계.
     * (자리 규칙을 아는 것은 그 함수뿐이라는 아래 `setupShown` 주석과 같은 이유다)
     */
    const onboardingMode = coachChores.length > 0 && composed.centerStage === 'NEW';

    /**
     * 실제로 **줄로 그리는** 강사 카드 — 오늘의 브리핑 화면과 같은 자리 규칙.
     *
     * 🔴 예전에는 `coachChores` 를 조건 없이 다 그렸다. 그래서 정착 센터에서도
     *    강사 줄이 4개씩 붙었고, 게다가 아래 세팅 축 줄과 **같이** 떴다
     *    (화면은 둘 중 하나만 그린다). 팝업이 세는 수와 그리는 줄이 갈렸다.
     *      신규   → 전부 (그 목록이 그날의 전부다 · 자르지 않는다)
     *      과도기 → 회원이 못 채운 자리만큼 (최대 2)
     *      정착   → 0
     */
    const shownChores = onboardingMode
        ? coachChores
        : coachChores.slice(0, Math.min(composed.openSlots, MAX_SETUP_SLOTS));

    const totalCount = onboardingMode
        ? coachChores.length
        : focus.length + (shownChores.length > 0 ? shownChores.length : setupCount);
    const setupOnly = !onboardingMode && focus.length === 0 && setupCount > 0;

    const goDetail = () => {
        onDismiss();
        navigate('/opsBriefing/today');
    };

    /*
     * ═══════════════════════════════════════════════════════════
     * 🔴 가린 팝업 — 잠긴 브라우저에서는 **건수만** 알린다
     * ═══════════════════════════════════════════════════════════
     * 여기서부터 아래로는 `focus` 를 **한 번도 읽지 않는다.** 회원 이름·상품·
     * 수치·금액이 한 글자도 나가면 안 된다 — 그게 이 분기의 존재 이유다.
     * 쓰는 값은 `focus.length` 하나뿐이고, 그건 「몇 건」이라는 사실이지
     * 누구인지가 아니다.
     *
     * ⚠️ 여기에 「위험 N건 포함」·「회원 N명」 같은 내역을 더하지 마세요.
     *    한 줄씩 늘리다 보면 결국 잠금이 무의미해집니다. 내역이 필요하면
     *    탭에 들어가서 인증하면 됩니다 — 그게 잠금의 뜻입니다.
     *
     * 「자세히 보기」는 브리핑 탭으로 보낼 뿐이고, 비밀번호는 **거기서** 묻는다.
     * 팝업이 직접 인증 창을 띄우면 매일 아침 모든 화면에서 비밀번호를 묻게 된다.
     */
    if (locked) {
        return (
            <div
                className="fixed inset-0 flex items-center justify-center"
                /* 팝업은 브리핑 레이아웃 밖(PrivateRoutes)이라 keep-all 을 여기서 잡는다 */
            style={{ background: 'rgba(12,22,40,0.6)', zIndex: 2000, padding: 24, wordBreak: 'keep-all' }}
                onClick={onDismiss}
            >
                <div
                    className="flex flex-col overflow-hidden rounded-[20px] bg-white"
                    style={{ width: 460, boxShadow: '0 32px 80px rgba(10,20,40,0.38)' }}
                    onClick={(e) => e.stopPropagation()}
                    role="dialog"
                    aria-label="손실 시그널 알림"
                >
                    <div
                        style={{
                            background: 'linear-gradient(135deg, #1A305D 0%, #24406F 55%, #2B5183 100%)',
                            padding: '26px 30px 24px',
                        }}
                    >
                        <span style={{ fontSize: 13, color: '#9DB4D8', letterSpacing: '0.03em' }}>
                            {today.format('YYYY년 M월 D일')} {weekday}요일 · 손실 시그널
                        </span>
                        <p
                            style={{
                                fontSize: 24,
                                fontWeight: 800,
                                color: '#FFFFFF',
                                marginTop: 8,
                                letterSpacing: '-0.02em',
                                lineHeight: '32px',
                            }}
                        >
                            오늘 챙길 일 <span style={{ color: '#FF8A92' }}>{totalCount}건</span>
                            {setupOnly ? '(센터 설정)' : ''}이 있습니다
                        </p>
                        <p style={{ fontSize: 13.5, color: '#9DB4D8', marginTop: 8, lineHeight: '20px' }}>
                            손실 시그널 탭에서 확인하세요
                        </p>
                    </div>

                    <div
                        className="flex items-center justify-between"
                        style={{ padding: '18px 30px 20px', gap: 12 }}
                    >
                        <button
                            type="button"
                            onClick={onMuteToday}
                            style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: 14,
                                color: '#7C8794',
                                textDecoration: 'underline',
                                textUnderlineOffset: 3,
                                padding: 0,
                            }}
                        >
                            오늘은 그만 보기
                        </button>
                        <div className="flex items-center" style={{ gap: 10 }}>
                            <button
                                type="button"
                                onClick={onDismiss}
                                className="rounded-[10px]"
                                style={{
                                    height: 42,
                                    padding: '0 18px',
                                    background: '#FFFFFF',
                                    border: '1px solid #DCE4EE',
                                    color: '#425063',
                                    fontSize: 15,
                                    cursor: 'pointer',
                                }}
                            >
                                나중에
                            </button>
                            <button
                                type="button"
                                onClick={goDetail}
                                className="rounded-[10px]"
                                style={{
                                    height: 42,
                                    padding: '0 22px',
                                    background: '#1A305D',
                                    border: 'none',
                                    color: '#FFFFFF',
                                    fontSize: 15,
                                    fontWeight: 700,
                                    cursor: 'pointer',
                                }}
                            >
                                손실 시그널 탭으로
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            className="fixed inset-0 flex items-center justify-center"
            /* 팝업은 브리핑 레이아웃 밖(PrivateRoutes)이라 keep-all 을 여기서 잡는다 */
            style={{ background: 'rgba(12,22,40,0.6)', zIndex: 2000, padding: 24, wordBreak: 'keep-all' }}
            onClick={onDismiss}
        >
            <div
                className="flex flex-col overflow-hidden rounded-[20px] bg-white"
                style={{
                    width: 940,
                    maxHeight: '92vh',
                    boxShadow: '0 32px 80px rgba(10,20,40,0.38)',
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* 헤더 */}
                <div
                    className="flex-none"
                    style={{
                        background: 'linear-gradient(135deg, #1A305D 0%, #24406F 55%, #2B5183 100%)',
                        padding: '30px 36px 28px',
                    }}
                >
                    <div className="flex items-center justify-between">
                        <div className="flex flex-col">
                            <span style={{ fontSize: 13.5, color: '#9DB4D8', letterSpacing: '0.03em' }}>
                                {today.format('YYYY년 M월 D일')} {weekday}요일 · 손실 시그널
                            </span>
                            {/*
                              주인공은 "몇 건이 쌓였나" 가 아니라 "오늘 이걸 하시면 됩니다" 다.
                              예전 문구("오늘 급하게 챙길 일이 29건 있습니다")는 아침에 여는
                              팝업에서 할 수 있는 일을 알려주는 대신 규모만 알려줬다.
                            */}
                            <span
                                style={{
                                    fontSize: 31,
                                    fontWeight: 800,
                                    color: '#FFFFFF',
                                    marginTop: 8,
                                    letterSpacing: '-0.03em',
                                    lineHeight: '40px',
                                }}
                            >
                                {onboardingMode ? (
                                    /* 신규 센터 — 강사 카드가 목록이다 (대표 시안) */
                                    <>
                                        오늘 챙길 일{' '}
                                        <span style={{ color: '#FF8A92' }}>{totalCount}건</span>입니다
                                    </>
                                ) : focus.length > 0 ? (
                                    <>
                                        오늘 집중할{' '}
                                        <span style={{ color: '#FF8A92' }}>{focus.length}건</span>입니다
                                    </>
                                ) : setupOnly ? (
                                    <>
                                        오늘은 <span style={{ color: '#FF8A92' }}>센터 설정 {setupCount}건</span>
                                        입니다
                                    </>
                                ) : (
                                    '오늘 확인하실 내용입니다'
                                )}
                            </span>
                            {/*
                              접었다는 사실은 밝힌다. 안 밝히면 "오늘은 다섯 건뿐이구나" 로 읽힌다.
                              위험 건수도 여기서 사실로만 적는다 — 머리말에서 크게 외치지 않는다.
                            */}
                            <span style={{ fontSize: 13.5, color: '#9DB4D8', marginTop: 7 }}>
                                {[
                                    urgent > 0 ? `위험 ${urgent}건 포함` : null,
                                    restCount > 0 ? `급한 순으로 골랐습니다 · 외 ${restCount}건` : null,
                                    /* 회원 데이터가 없어서 세팅이 오늘 할 일이라는 사실을 밝힌다 */
                                    setupOnly || onboardingMode
                                        ? '회원 데이터가 쌓이기 전까지는 센터 설정이 오늘의 할 일입니다'
                                        : null,
                                ]
                                    .filter(Boolean)
                                    .join('  ·  ')}
                            </span>
                        </div>

                        <div className="flex items-start" style={{ gap: 0 }}>
                            {[
                                { label: '회원', value: memberCount, unit: '명' },
                                { label: '강사', value: coachCount, unit: '명' },
                            ].map((stat, i) => (
                                <div
                                    key={stat.label}
                                    className="flex flex-col items-center"
                                    style={{
                                        padding: '0 24px',
                                        borderLeft: i === 0 ? 'none' : '1px solid rgba(255,255,255,0.14)',
                                    }}
                                >
                                    <span style={{ fontSize: 12.5, color: '#9DB4D8' }}>{stat.label}</span>
                                    <div className="flex items-baseline" style={{ gap: 3, marginTop: 4 }}>
                                        <span style={{ fontSize: 25, fontWeight: 800, color: '#FFFFFF' }}>
                                            {stat.value}
                                        </span>
                                        <span style={{ fontSize: 12.5, color: '#9DB4D8' }}>{stat.unit}</span>
                                    </div>
                                </div>
                            ))}
                            <button
                                type="button"
                                onClick={onDismiss}
                                aria-label="닫기"
                                style={{
                                    marginLeft: 18,
                                    border: 'none',
                                    background: 'rgba(255,255,255,0.14)',
                                    color: '#FFFFFF',
                                    width: 34,
                                    height: 34,
                                    borderRadius: 9,
                                    cursor: 'pointer',
                                    fontSize: 18,
                                    lineHeight: '34px',
                                }}
                            >
                                ×
                            </button>
                        </div>
                    </div>
                </div>

                {/* 항목 목록 */}
                <div className="flex flex-col overflow-y-auto" style={{ padding: '10px 20px 12px' }}>
                    {/*
                      🔴 **세팅 과제가 먼저 선다 — 화면과 같은 순서다** (QA34).

                      오늘의 브리핑도 과제 카드를 회원 카드 **위**에 세우고
                      번호도 거기서 1 부터 시작한다. 팝업만 순서가 다르면
                      「팝업에서 본 첫 줄」과 「화면의 첫 줄」이 어긋난다.

                      예전에는 과제를 아예 안 그리고 건수만 말했다. 그래서 신규
                      센터의 팝업은 「센터 설정 7건입니다」라고 해 놓고 아래가
                      비어 있었다 — 무엇 7건인지 알 수 없는 화면이었다.

                      ⚠️ 회원 줄과 달리 **이름·수치를 크게 그리지 않는다.** 이건
                         팝업이지 카드가 아니고, 자세한 것은 탭에서 본다.
                    */}
                    {/*
                      신규 센터 — 강사 카드가 목록이다. 화면과 **같은 순서**로 선다.
                      자세한 셀·회원 줄은 탭에서 본다. 여기서는 「누구의 무엇이
                      막혔나」 한 줄이면 충분하다.
                    */}
                    {shownChores.map((v) => (
                        <div
                            key={v.row.coachId}
                            aria-label={`세팅 과제 · ${v.row.name} 강사`}
                            className="flex items-center rounded-[12px]"
                            style={{
                                border: '1px solid #C9D9EE',
                                background: '#F2F7FF',
                                padding: '12px 14px',
                                marginBottom: 8,
                                gap: 10,
                            }}
                        >
                            <span
                                className="rounded-[6px]"
                                style={{
                                    background: v.actor.tone === 'coach' ? '#FCF3E0' : '#DCE7F8',
                                    color: v.actor.tone === 'coach' ? '#9A6A00' : '#2C4C8C',
                                    fontSize: 11.5,
                                    fontWeight: 800,
                                    padding: '3px 8px',
                                    flex: 'none',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {v.actor.label}
                            </span>
                            <span
                                style={{ fontSize: 14.5, fontWeight: 800, color: '#16202E', flex: 'none' }}
                            >
                                {v.row.name}
                            </span>
                            <span
                                className="min-w-0 flex-1"
                                style={{ fontSize: 13, fontWeight: 600, color: '#5B6B7F', wordBreak: 'keep-all' }}
                            >
                                {v.headline}
                            </span>
                        </div>
                    ))}

                    {/*
                      🔴 **강사 줄이 섰으면 축 줄은 그리지 않는다** (2026-09-02 QA36).
                         오늘의 브리핑 화면이 `강사 카드 : 세팅 축 카드` 로 둘 중
                         하나만 그린다. 팝업만 둘을 겹쳐 그려서 같은 세팅 과제가
                         두 번 나왔고, 「N건」은 한쪽만 세고 있었다.
                    */}
                    {(shownChores.length > 0 ? [] : setupShown).map((task) => (
                        <div
                            key={task.id}
                            /* 카드(`SetupTaskCard`)와 같은 규칙의 이름 — 테스트·보조기기가 집는다 */
                            aria-label={`세팅 과제 · ${task.title}`}
                            className="flex items-center rounded-[12px]"
                            style={{
                                border: '1px solid #C9D9EE',
                                background: '#F2F7FF',
                                padding: '12px 14px',
                                marginBottom: 8,
                                gap: 10,
                            }}
                        >
                            <span
                                className="rounded-[6px]"
                                style={{
                                    background: '#DCE7F8',
                                    color: '#2C4C8C',
                                    fontSize: 11.5,
                                    fontWeight: 800,
                                    padding: '3px 8px',
                                    flex: 'none',
                                }}
                            >
                                {task.task === 'ONBOARDING' ? '센터 설정' : '강사 정착'}
                            </span>
                            <span
                                className="min-w-0 flex-1"
                                style={{
                                    fontSize: 14.5,
                                    fontWeight: 700,
                                    color: '#16202E',
                                    wordBreak: 'keep-all',
                                }}
                            >
                                {task.title}
                            </span>
                            {task.count != null && (
                                <span
                                    className="flex items-baseline"
                                    style={{ gap: 2, flex: 'none', whiteSpace: 'nowrap' }}
                                >
                                    <span
                                        style={{
                                            fontSize: 17,
                                            fontWeight: 800,
                                            color: '#2C4C8C',
                                            letterSpacing: '-0.02em',
                                        }}
                                    >
                                        {task.count.toLocaleString('ko-KR')}
                                    </span>
                                    <span style={{ fontSize: 12, color: '#5B7CB8', fontWeight: 700 }}>
                                        명
                                    </span>
                                </span>
                            )}
                        </div>
                    ))}

                    {focus.map((item) => {
                        const c = GRADE_COLOR[item.grade];
                        const m = item.keyMetric;
                        const comparable = canCompareDelta(m);
                        return (
                            <div
                                key={item.itemId}
                                className="flex items-center rounded-[12px] transition-colors"
                                style={{
                                    padding: '18px 16px',
                                    gap: 18,
                                    borderBottom: '1px solid #F1F4F9',
                                }}
                            >
                                {/* 등급 레일 */}
                                <div
                                    style={{
                                        width: 4,
                                        height: 52,
                                        borderRadius: 2,
                                        background: c.rail,
                                        flex: 'none',
                                    }}
                                />

                                {/* 대상 + 판단 */}
                                <div className="flex min-w-0 flex-1 flex-col">
                                    <div className="flex items-center" style={{ gap: 8 }}>
                                        <span
                                            className="rounded-[5px]"
                                            style={{
                                                background: c.bg,
                                                color: c.accent,
                                                fontSize: 11.5,
                                                fontWeight: 800,
                                                padding: '3px 9px',
                                            }}
                                        >
                                            {GRADE_LABEL[item.grade]}
                                        </span>
                                        <span style={{ fontSize: 17, fontWeight: 700, color: '#16202E' }}>
                                            {item.subjectName}
                                            {item.subjectType === 'MEMBER' ? ' 회원' : ' 강사'}
                                        </span>
                                        {item.relatedCoachName && (
                                            <span style={{ fontSize: 14, color: '#93A0AE' }}>
                                                담당 {item.relatedCoachName}
                                            </span>
                                        )}
                                    </div>

                                    <span
                                        style={{
                                            fontSize: 18.5,
                                            fontWeight: 700,
                                            color: '#0F1723',
                                            marginTop: 7,
                                            letterSpacing: '-0.02em',
                                        }}
                                    >
                                        {item.title}
                                    </span>

                                    {item.subMetrics && item.subMetrics.length > 0 && (
                                        <div className="flex items-center" style={{ gap: 14, marginTop: 7 }}>
                                            {item.subMetrics.map((sm, i) => (
                                                <span key={i} style={{ fontSize: 14, color: '#7C8794' }}>
                                                    {sm.label}{' '}
                                                    <span
                                                        style={{
                                                            fontWeight: 700,
                                                            color: sm.alert ? c.accent : '#2C3A4B',
                                                        }}
                                                    >
                                                        {sm.value}
                                                    </span>
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* 지표 */}
                                <div
                                    className="flex flex-none items-center"
                                    style={{ gap: 20, paddingLeft: 20, borderLeft: '1px solid #F1F4F9' }}
                                >
                                    <div className="flex flex-col" style={{ width: 152 }}>
                                        <span style={{ fontSize: 11.5, color: '#93A0AE' }}>{m.label}</span>
                                        <div className="flex items-baseline" style={{ gap: 7, marginTop: 3 }}>
                                            <span style={{ fontSize: 16, fontWeight: 700, color: '#A7B0BC' }}>
                                                {/* 등록 전 구간이면 '0회' 가 아니라 '등록 전' 이다 */}
                                                {beforeValueOf(m)}
                                            </span>
                                            <span style={{ fontSize: 13, color: '#C4CCD8' }}>→</span>
                                            <span
                                                style={{
                                                    fontSize: 26,
                                                    fontWeight: 800,
                                                    letterSpacing: '-0.03em',
                                                    color: m.alert ? c.accent : '#16202E',
                                                }}
                                            >
                                                {m.afterValue}
                                            </span>
                                        </div>
                                        <span
                                            className="inline-flex items-center rounded-[6px]"
                                            style={{
                                                marginTop: 6,
                                                alignSelf: 'flex-start',
                                                background: !comparable
                                                    ? '#F1F4F9'
                                                    : m.alert
                                                      ? c.bg
                                                      : '#E9F6F2',
                                                color: !comparable
                                                    ? '#7C8794'
                                                    : m.alert
                                                      ? c.accent
                                                      : '#00A793',
                                                fontSize: 13,
                                                fontWeight: 800,
                                                padding: '3px 9px',
                                            }}
                                        >
                                            {/* 등록 전 구간과 비교한 변화율은 근거 없는 숫자다 */}
                                            {comparable
                                                ? `${m.deltaPct < 0 ? '▼' : '▲'} ${Math.abs(m.deltaPct)}%`
                                                : BEFORE_UNCOVERED_DELTA_TEXT}
                                        </span>
                                    </div>

                                    <div className="flex flex-col items-center">
                                        <MiniTrendBars
                                            data={m.trend}
                                            color={m.alert ? c.accent : '#3D73DD'}
                                        />
                                        <span style={{ fontSize: 10.5, color: '#B4BDC8', marginTop: 5 }}>
                                            최근 8주
                                        </span>
                                    </div>
                                </div>
                            </div>
                        );
                    })}

                    {/*
                      접힌 건이 있으면 목록 끝에서 한 번 더 말한다.
                      머리말은 팝업을 여는 순간 읽히고, 이 줄은 다 훑고 난 뒤에 읽힌다 —
                      "이게 전부구나" 하고 닫는 것을 막는 자리다.
                      팝업에서는 펼치지 않는다. 여기는 고르는 자리가 아니라 여는 자리라,
                      전체 목록은 '자세히 보기' 로 브리핑 화면에서 본다.
                    */}
                    {restCount > 0 && (
                        <div style={{ padding: '14px 16px 4px', textAlign: 'center' }}>
                            <span style={{ fontSize: 13.5, color: '#93A0AE' }}>
                                외 {restCount}건은 오늘의 시그널 화면에서 볼 수 있습니다
                            </span>
                        </div>
                    )}
                </div>

                {/* 하단 */}
                <div
                    className="flex flex-none items-center justify-between"
                    style={{ padding: '18px 36px 22px', borderTop: '1px solid #EDF1F7', background: '#FBFCFE' }}
                >
                    <div className="flex flex-col">
                        <button
                            type="button"
                            onClick={onMuteToday}
                            style={{
                                border: 'none',
                                background: 'transparent',
                                cursor: 'pointer',
                                fontSize: 14.5,
                                color: '#7C8794',
                                textDecoration: 'underline',
                                textUnderlineOffset: 3,
                                padding: 0,
                                textAlign: 'left',
                            }}
                        >
                            오늘은 그만 보기
                        </button>
                        <span style={{ fontSize: 12.5, color: '#A7B0BC', marginTop: 5 }}>
                            그냥 닫으면 다른 화면으로 이동할 때 다시 알려드립니다
                        </span>
                    </div>

                    <div className="flex items-center" style={{ gap: 10 }}>
                        <button
                            type="button"
                            onClick={onDismiss}
                            className="rounded-[10px]"
                            style={{
                                height: 46,
                                padding: '0 20px',
                                background: '#FFFFFF',
                                border: '1px solid #DCE4EE',
                                color: '#425063',
                                fontSize: 15.5,
                                cursor: 'pointer',
                            }}
                        >
                            나중에
                        </button>
                        <button
                            type="button"
                            onClick={goDetail}
                            className="rounded-[10px]"
                            style={{
                                height: 46,
                                padding: '0 28px',
                                background: '#1A305D',
                                border: 'none',
                                color: '#FFFFFF',
                                fontSize: 16,
                                fontWeight: 700,
                                cursor: 'pointer',
                                boxShadow: '0 8px 20px rgba(26,48,93,0.26)',
                            }}
                        >
                            자세히 보기
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MorningBriefingModal;
